import { useEffect, useRef, useState } from "react";
import FilePicker from "@/components/FilePicker";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import AccordionCard from "@/components/AccordionCard";
import {
  Plus, Trash, PaperPlaneTilt, CheckCircle, Circle, DownloadSimple,
} from "@phosphor-icons/react";

const fmtDate = (d) => {
  if (!d) return "—";
  const p = new Date(d);
  if (isNaN(p.getTime())) return d;
  return p.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function DueBadge({ due, status }) {
  if (!due) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(due);
  const days = Math.floor((d - today) / 86400000);
  let bg = "#e5e7eb", color = "#374151", label = fmtDate(due);
  if (status !== "complete") {
    if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label = `Overdue ${-days}d`; }
    else if (days <= 2) { bg = "#fef3c7"; color = "#78350f"; label = days === 0 ? "Due today" : `Due in ${days}d`; }
  }
  return <span className="label px-2 py-0.5" style={{ background: bg, color, border: `1px solid ${bg}` }}>{label}</span>;
}

async function downloadFile(fileId, filename) {
  try {
    const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "file"; a.click();
    URL.revokeObjectURL(url);
  } catch (_e) { toast.error("Download failed"); }
}

function SubmittedTaskCard({ task, onChanged }) {
  const toggleSub = async (s) => {
    try {
      await api.patch(`/me/admin-tasks/${task.task_id}/subtasks/${s.subtask_id}`,
        { title: s.title, done: !s.done });
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };
  const uploadSubFile = async (s, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/admin-tasks/${task.task_id}/subtasks/${s.subtask_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Uploaded");
      onChanged && onChanged();
    } catch (_e) { toast.error("Upload failed"); }
  };
  const header = (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="label px-2 py-0.5 border" style={{ borderColor: "var(--hair)", fontWeight: 700 }}>
          {task.status.replace("_", " ")}
        </span>
        <h4 className="title-xl text-lg" style={{ fontWeight: 700 }}>{task.title}</h4>
        <DueBadge due={task.due_date} status={task.status} />
      </div>
      <p className="label mt-1">
        Submitted {fmtDate(task.created_at)}
        {(task.subtasks || []).length > 0 &&
          ` · ${task.subtasks.filter(s => s.done).length}/${task.subtasks.length} subtasks`}
      </p>
    </div>
  );
  return (
    <AccordionCard testid={`submitted-task-${task.task_id}`} header={header}>
      {task.description && (
        <p className="whitespace-pre-wrap text-sm" style={{ color: "var(--text-dim)" }}>{task.description}</p>
      )}
      {(task.subtasks || []).length > 0 && (
        <div className="mt-3 space-y-2">
          {task.subtasks.map(s => (
            <div key={s.subtask_id} className="hair p-2"
              data-testid={`submitted-sub-${s.subtask_id}`}>
              <div className="flex items-start gap-2 text-sm">
                <button type="button" onClick={() => toggleSub(s)}
                  className="btn-ghost text-xs p-1 mt-0.5">
                  {s.done ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} weight="bold" />}
                </button>
                <div className="flex-1">
                  <span style={{ textDecoration: s.done ? "line-through" : "none",
                                 color: s.done ? "var(--text-dim)" : "inherit" }}>{s.title}</span>
                  {s.due_date && (
                    <span className="ml-2"><DueBadge due={s.due_date} status={s.done ? "complete" : ""} /></span>
                  )}
                  {s.notes && (
                    <p className="text-xs mt-0.5" style={{ color: "var(--text-dim)" }}>{s.notes}</p>
                  )}
                  {(s.files || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.files.map(f => (
                        <button key={f.file_id} type="button"
                          onClick={() => downloadFile(f.file_id, f.filename)}
                          className="btn-ghost text-xs"
                          data-testid={`submitted-sub-file-${f.file_id}`}>
                          <DownloadSimple size={10} weight="bold" /> {f.filename}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <label className="btn-ghost text-xs p-1 cursor-pointer" title="Attach files"
                  data-testid={`submitted-sub-upload-label-${s.subtask_id}`}>
                  <Plus size={11} weight="bold" />
                  <input type="file" multiple className="hidden"
                    data-testid={`submitted-sub-upload-${s.subtask_id}`}
                    onChange={(e) => { uploadSubFile(s, e.target.files); e.target.value = ""; }} />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
      {(task.files || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {task.files.map(f => (
            <button key={f.file_id} type="button"
              onClick={() => downloadFile(f.file_id, f.filename)}
              className="btn-ghost text-xs"
              data-testid={`submitted-file-${f.file_id}`}>
              <DownloadSimple size={11} weight="bold" /> {f.filename}
            </button>
          ))}
        </div>
      )}
    </AccordionCard>
  );
}

function SubmitTaskForm({ onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "normal" });
  const [subs, setSubs] = useState([""]);
  const [busy, setBusy] = useState(false);
  const filesRef = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/me/admin-tasks/submit", {
        ...form,
        due_date: form.due_date || null,
        subtasks: subs.filter(s => s.trim()).map(s => ({ title: s.trim() })),
      });
      // Upload attachments (if any) to the newly created task
      const files = Array.from(filesRef.current?.files || []);
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/admin-tasks/${data.task_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Task submitted to your team");
      setForm({ title: "", description: "", due_date: "", priority: "normal" });
      setSubs([""]);
      if (filesRef.current) filesRef.current.value = "";
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="panel p-5 space-y-3" data-testid="submit-task-form">
      <div className="flex items-center gap-2">
        <Plus size={16} weight="bold" />
        <h3 className="title-xl text-xl">Submit a task to your team</h3>
      </div>
      <input required placeholder="Title" value={form.title}
        data-testid="submit-task-title"
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        className="w-full px-3 py-2" />
      <textarea placeholder="Notes / details" value={form.description} rows={3}
        data-testid="submit-task-desc"
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        className="w-full px-3 py-2" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <p className="label mb-1">Priority</p>
          <select value={form.priority} data-testid="submit-task-priority"
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="w-full px-3 py-2">
            {["low", "normal", "high"].map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <p className="label mb-1">Requested due (optional)</p>
          <input type="date" value={form.due_date} data-testid="submit-task-due"
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="w-full px-3 py-2" />
        </div>
      </div>
      <div>
        <p className="label mb-1">Subtasks</p>
        <div className="space-y-1">
          {subs.map((s, i) => (
            <div key={i} className="flex gap-2">
              <input value={s} placeholder={`Subtask ${i + 1}`}
                data-testid={`submit-task-sub-${i}`}
                onChange={(e) => setSubs(subs.map((x, j) => j === i ? e.target.value : x))}
                className="flex-1 px-3 py-2 text-sm" />
              <button type="button" onClick={() => setSubs(subs.filter((_, j) => j !== i))}
                className="btn-ghost text-xs"><Trash size={12} /></button>
            </div>
          ))}
          <button type="button" onClick={() => setSubs([...subs, ""])}
            className="btn-ghost text-xs">
            <Plus size={12} /> Add subtask
          </button>
        </div>
      </div>
      <div>
        <p className="label mb-1">Attachments (optional, multiple)</p>
        <FilePicker ref={filesRef} testid="submit-task-files" />
      </div>
      <button disabled={busy} data-testid="submit-task-btn"
        className="btn-primary justify-center w-full h-12">
        <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Sending…" : "Submit task"}
      </button>
    </form>
  );
}

export default function SubmitTaskPanel() {
  const [tasks, setTasks] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/me/admin-tasks");
      setTasks(data);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  // Split tasks into submitted-by-me vs assigned-by-team (both come from /me/admin-tasks)
  const submitted = tasks.filter(t => t.submitted_by === "client");
  const open = submitted.filter(t => t.status !== "complete");
  const done = submitted.filter(t => t.status === "complete");

  return (
    <div className="space-y-6" data-testid="submit-task-panel">
      <div>
        <h2 className="title-xl text-2xl mb-2">Submit a task</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-dim)" }}>
          Send a request or task to your team. You'll get an email when they respond.
        </p>
      </div>
      <SubmitTaskForm onCreated={load} />
      {(open.length > 0 || done.length > 0) && (
        <div className="space-y-4">
          {open.length > 0 && (
            <div>
              <h3 className="title-xl text-xl mb-2">In progress · {open.length}</h3>
              <div className="space-y-2">
                {open.map(t => <SubmittedTaskCard key={t.task_id} task={t} onChanged={load} />)}
              </div>
            </div>
          )}
          {done.length > 0 && (
            <div>
              <h3 className="title-xl text-xl mb-2">Completed · {done.length}</h3>
              <div className="space-y-2">
                {done.map(t => <SubmittedTaskCard key={t.task_id} task={t} onChanged={load} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
